import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/feedback";
import { formatMoney } from "@/features/invoicing/money";
import type { Asset, LineKind } from "@/features/invoicing/types";
import { COPY } from "./copy";
import type { DraftLine, PricedLine } from "./lines";

/**
 * The lines section of `/stablecoins/invoices/new`.
 *
 * Three kinds, and the shape of the row follows the kind rather than one
 * universal row with half its fields blank:
 *
 *  - `ITEM` — description, quantity, unit price. The line total is
 *    `quantityMilli × unitPrice / 1000` and has to land on a whole base unit;
 *    when it does not, the row says so and the total column stays empty rather
 *    than showing a rounded figure (see `lines.ts`).
 *  - `TAX` and `DISCOUNT` — **flat** lines: a description and an amount (INV-01,
 *    G14). A tax or discount row therefore never shows a quantity or a unit
 *    price, because a rate is not what is on the wire.
 *
 * Starting state is **zero lines** with the empty state below: the spec lists
 * "empty (no line items yet)" as a real state of this page, and it is the
 * honest first render — an invoice with a pre-filled example line would be the
 * page inventing a product the operator did not sell.
 *
 * Every value on a row is a string the operator typed. Conversion to base units
 * happens in `lines.ts` at submit time, once, so there is no second place where
 * a typed decimal becomes a number.
 */

let lineSequence = 0;

/** A fresh row of the given kind. Ids are local to the form and never sent. */
export function newDraftLine(kind: LineKind): DraftLine {
  lineSequence += 1;
  return { id: `line-${lineSequence}`, kind, description: "", quantity: "", unitPrice: "", amount: "" };
}

const KIND_LABEL: Record<LineKind, string> = {
  ITEM: COPY.lineKindItem,
  TAX: COPY.lineKindTax,
  DISCOUNT: COPY.lineKindDiscount,
};

export interface LineEditorProps {
  rows: DraftLine[];
  /** `rows`, priced by `previewInvoice`, in the same order. */
  priced: PricedLine[];
  /** The asset the rows are denominated in; null before one is chosen. */
  asset: Asset | null;
  onChange: (rows: DraftLine[]) => void;
  disabled: boolean;
}

export function LineEditor({ rows, priced, asset, onChange, disabled }: LineEditorProps) {
  function update(id: string, patch: Partial<DraftLine>) {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  function remove(id: string) {
    onChange(rows.filter((row) => row.id !== id));
  }

  function add(kind: LineKind) {
    onChange([...rows, newDraftLine(kind)]);
  }

  return (
    <div className="flex flex-col gap-3">
      {rows.length === 0 ? (
        <EmptyState
          className="!py-10"
          title={COPY.linesEmptyTitle}
          description={COPY.linesEmptyDescription}
        />
      ) : (
        <ul className="flex flex-col gap-3" data-testid="invoice-lines">
          {rows.map((row, index) => {
            const line = priced[index];
            const position = index + 1;
            const label = KIND_LABEL[row.kind];
            return (
              <li
                key={row.id}
                className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-3.5"
                data-testid={`invoice-line-${position}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="rounded-full border border-line bg-bg px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.05em] text-muted">
                    {label}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    iconOnly
                    disabled={disabled}
                    aria-label={`${COPY.removeLine}: ${label} ${position}`}
                    onClick={() => remove(row.id)}
                  >
                    <Trash2 aria-hidden />
                  </Button>
                </div>

                <Field label={COPY.lineDescription} htmlFor={`${row.id}-description`} required>
                  <Input
                    id={`${row.id}-description`}
                    aria-label={`Line ${position} description`}
                    value={row.description}
                    disabled={disabled}
                    error={Boolean(line?.error && row.description.trim() === "")}
                    onChange={(event) => update(row.id, { description: event.target.value })}
                  />
                </Field>

                {row.kind === "ITEM" ? (
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field label={COPY.lineQuantity} htmlFor={`${row.id}-quantity`} required>
                      <Input
                        id={`${row.id}-quantity`}
                        aria-label={`Line ${position} quantity`}
                        inputMode="decimal"
                        value={row.quantity}
                        disabled={disabled}
                        onChange={(event) => update(row.id, { quantity: event.target.value })}
                      />
                    </Field>
                    <Field
                      label={COPY.lineUnitPrice}
                      htmlFor={`${row.id}-unit-price`}
                      required
                      hint={asset ? asset.symbol : undefined}
                    >
                      <Input
                        id={`${row.id}-unit-price`}
                        aria-label={`Line ${position} unit price`}
                        inputMode="decimal"
                        value={row.unitPrice}
                        disabled={disabled}
                        onChange={(event) => update(row.id, { unitPrice: event.target.value })}
                      />
                    </Field>
                    <Field label={COPY.lineTotal} htmlFor={`${row.id}-total`}>
                      <Input
                        id={`${row.id}-total`}
                        readOnly
                        aria-label={`Line ${position} total`}
                        value={line?.lineTotal ? formatMoney(line.lineTotal) : ""}
                      />
                    </Field>
                  </div>
                ) : (
                  <Field label={COPY.lineAmount} htmlFor={`${row.id}-amount`} required>
                    <Input
                      id={`${row.id}-amount`}
                      aria-label={`Line ${position} amount`}
                      inputMode="decimal"
                      value={row.amount}
                      disabled={disabled}
                      onChange={(event) => update(row.id, { amount: event.target.value })}
                    />
                  </Field>
                )}

                {line?.error && (
                  <p className="text-xs text-danger" role="alert">
                    {line.error.message}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => add("ITEM")}>
          <Plus aria-hidden />
          {COPY.addItem}
        </Button>
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => add("TAX")}>
          <Plus aria-hidden />
          {COPY.addTax}
        </Button>
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => add("DISCOUNT")}>
          <Plus aria-hidden />
          {COPY.addDiscount}
        </Button>
      </div>
    </div>
  );
}
